import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ORDER_TYPE } from '@/constants/order_type'
import { QUERIES } from '@/constants/queries'
import { REQUEST_STATUS } from '@/constants/request-status'
import { REQUEST_TYPE } from '@/constants/request-type'
import { getRequests } from '@/services/requests/get-requests'
import { getRequestsKpis } from '@/services/requests/get-requests-kpis'
import { useAuthStore } from '@/store/auth-store'
import type { Request } from '@/types/requests/request'
import type { TableData } from '@/types/table/table-data'
import { basicDataCellFormatter } from '@/utils/basic-data-cell-formatter'
import { createTableConfig } from '@/utils/create-table-config'
import { getDeadlineColorByName } from '@/utils/get-deadline-color-by-id'
import { getStatusColorById } from '@/utils/get-status-color-by-id'
import { getStatusLabelById } from '@/utils/get-status-label-by-id'
import { getUrlRedirectByRequest } from '@/utils/get-url-redirect-by-request'

function createTable(data: Request[] | undefined) {
  const tableData: TableData<Request>[] =
    data?.map((item) => {
      const urlToRedirect = getUrlRedirectByRequest(item)

      return {
        data: item,
        redirectURL: urlToRedirect
      }
    }) || []

  return createTableConfig<Request>(
    [
      { key: 'student.person.name', label: 'Nome do Estudante', hidden: false },
      {
        key: 'createdAt',
        label: 'Data da Abertura',
        toText: basicDataCellFormatter,
        hidden: false
      },
      {
        key: 'deadline',
        label: 'Prazo',
        hidden: false,
        getCustomStyle: getDeadlineColorByName
      },
      { key: 'student.ra', label: 'RA', hidden: false },
      {
        key: 'previousEducation.name',
        label: 'Curso Antigo',
        hidden: false
      },
      {
        key: 'desiredEducation.desiredEducation',
        label: 'Curso Desejado',
        hidden: false
      },
      {
        key: 'previousEducation.institution',
        label: 'IES Anterior',
        hidden: false
      },
      {
        key: 'requestStatus.id',
        label: 'Status',
        getCustomStyle: getStatusColorById,
        toText: getStatusLabelById,
        hidden: false
      }
    ],
    tableData
  )
}

export const useMyReviews = () => {
  const [kpiStartDate, setKpiStartDate] = useState<Date | undefined>()
  const [kpiEndDate, setKpiEndDate] = useState<Date | undefined>()
  const { id } = useAuthStore()

  const {
    data: externalTransferKpiData,
    isLoading: externalTransferIsLoading,
    refetch: refetchExternalTransferKpi,
    isRefetching: isRefetchingExternalTransferKpi
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_EXTERNAL_TRANSFER_MANAGER_PANEL_KPI],
    queryFn: () => {
      return getRequestsKpis({
        requestType: REQUEST_TYPE.EXTERNAL_TRANSFER.id,
        managerId: id!,
        startDate: kpiStartDate,
        endDate: kpiEndDate
      })
    },
    enabled: !!kpiStartDate && !!kpiEndDate
  })

  const {
    data: changeOfMajorKpiData,
    isLoading: changeOfMajorIsLoading,
    refetch: refetchChangeOfMajorKpi,
    isRefetching: isRefetchingChangeOfMajorKpi
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_CHANGE_OF_MAJOR_MANAGER_PANEL_KPI],
    queryFn: () => {
      return getRequestsKpis({
        requestType: REQUEST_TYPE.CHANGE_OF_MAJOR.id,
        startDate: kpiStartDate,
        managerId: id!,
        endDate: kpiEndDate
      })
    },
    enabled: !!kpiStartDate && !!kpiEndDate
  })

  const {
    data: creditTransferKpiData,
    isLoading: creditTransferIsLoading,
    refetch: refetchCreditTransferKpi,
    isRefetching: isRefetchingCreditTransferKpi
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_CREDIT_TRANSFER_MANAGER_PANEL_KPI],
    queryFn: () => {
      return getRequestsKpis({
        requestType: REQUEST_TYPE.CREDIT_TRANSFER.id,
        startDate: kpiStartDate,
        managerId: id!,
        endDate: kpiEndDate
      })
    },
    enabled: !!kpiStartDate && !!kpiEndDate
  })

  const refetchKpis = () => {
    refetchChangeOfMajorKpi()
    refetchCreditTransferKpi()
    refetchExternalTransferKpi()
  }

  const isRefetchingKpis =
    isRefetchingChangeOfMajorKpi
    || isRefetchingCreditTransferKpi
    || isRefetchingExternalTransferKpi
    || creditTransferIsLoading
    || externalTransferIsLoading
    || changeOfMajorIsLoading

  const {
    data: requestData,
    isLoading: requestIsLoading,
    isFetching: requestIsFetching
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_MY_REVIEWS],
    queryFn: () => {
      return getRequests({
        page: '1',
        limit: '10',
        orderBy: 'createdAt',
        managerId: id!,
        orderType: ORDER_TYPE.ASC,
        firstStatusId: REQUEST_STATUS.UNDER_REVIEW.id
      })
    }
  })

  const pageIsLoading =
    externalTransferIsLoading
    || changeOfMajorIsLoading
    || creditTransferIsLoading
    || requestIsLoading

  return {
    states: {
      externalTransferKpiData,
      changeOfMajorKpiData,
      creditTransferKpiData,
      isRefetchingKpis,
      requestIsLoading,
      requestData,
      kpiStartDate,
      pageIsLoading,
      kpiEndDate,
      requestIsFetching
    },
    actions: { createTable, setKpiStartDate, setKpiEndDate, refetchKpis }
  }
}
